// The light/dark choice is remembered per signed-in user in this browser, so people who share a computer keep their own.
// 'uiTheme' is what older versions saved for the whole browser; it is handed to the first user who signs in, then removed.

export type ThemeChoice = 'light' | 'dark';

const LEGACY_KEY = 'uiTheme';
const userKey = (username: string): string => `uiTheme:${username.trim().toLowerCase()}`;

export const isThemeChoice = (value: unknown): value is ThemeChoice => value === 'light' || value === 'dark';

const read = (key: string): ThemeChoice | undefined => {
  try {
    const value = localStorage.getItem(key);
    return isThemeChoice(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

/** The theme this browser's OS prefers. */
export const systemTheme = (): ThemeChoice => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

/** The theme this user chose, if they have chosen one. */
export const readUserTheme = (username: string): ThemeChoice | undefined => read(userKey(username));

/** Remember this user's choice. A browser that cannot store it simply forgets it at the end of the session. */
export const saveUserTheme = (username: string, theme: ThemeChoice): void => {
  try {
    localStorage.setItem(userKey(username), theme);
  } catch {
    // storage is full or blocked: the theme still applies for this session
  }
};

/** The theme to show this user: their own choice, else the browser-wide one an older version saved (then claimed for them), else the OS preference. */
export const themeForUser = (username: string): ThemeChoice => {
  const own = readUserTheme(username);
  if (own) return own;
  const legacy = read(LEGACY_KEY);
  if (legacy) {
    saveUserTheme(username, legacy);
    try {
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      // nothing to clean up
    }
    return legacy;
  }
  return systemTheme();
};

/** The username of whoever is signed in, read before the app has loaded its state. */
export const storedUsername = (): string | undefined => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}') as { username?: string; emailId?: string };
    return user.username || user.emailId || undefined;
  } catch {
    return undefined;
  }
};
